import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions, GEMINI_API_KEY } from '../config/firebase';

// --- Notification Helper ---
export const createNotification = (userId, message, destination = null) => {
    if (!userId) return Promise.resolve();
    return addDoc(collection(db, 'notifications'), {
        userId,
        message,
        isRead: false,
        createdAt: serverTimestamp(),
        destination
    });
};

export const sanitizeData = (data) => {
    const clean = {};
    Object.keys(data).forEach(key => {
        if (data[key] !== undefined) {
            clean[key] = data[key];
        }
    });
    return clean;
};

// --- Geocoding & Distance Helpers ---
export const geocodeAddress = async (address) => {
    if (!window.google || !window.google.maps || !window.google.maps.Geocoder) return null;
    const geocoder = new window.google.maps.Geocoder();
    return new Promise((resolve) => {
        geocoder.geocode({ address }, (results, status) => {
            if (status === 'OK' && results[0]) {
                resolve({
                    lat: results[0].geometry.location.lat(),
                    lng: results[0].geometry.location.lng()
                });
            } else {
                resolve(null);
            }
        });
    });
};

export const getDistance = (p1, p2) => {
    if (!p1 || !p2) return Infinity;
    const R = 3958.8; // Radius of the Earth in miles
    const dLat = (p2.lat - p1.lat) * Math.PI / 180;
    const dLon = (p2.lng - p1.lng) * Math.PI / 180;
    const a = 
        Math.sin(dLat/2) * Math.sin(dLat/2) +
        Math.cos(p1.lat * Math.PI / 180) * Math.cos(p2.lat * Math.PI / 180) * Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)); 
    return R * c; 
};

// --- Tax Rate Helper ---
export const getTaxRate = (zip) => {
    if (!zip) return 0.0;
    const cleanZip = zip.toString().replace(/[^0-9]/g, '').slice(0, 5);
    if (cleanZip.length < 3) return 0.065; // Fallback if invalid
    
    const prefix = parseInt(cleanZip.substring(0, 3));
    
    // CA (California) 900-961
    if (prefix >= 900 && prefix <= 961) return 0.0882; 
    // NY (New York) 100-149
    if (prefix >= 100 && prefix <= 149) return 0.0852; 
    // TX (Texas) 750-799
    if (prefix >= 750 && prefix <= 799) return 0.0820; 
    // FL (Florida) 320-349
    if (prefix >= 320 && prefix <= 349) return 0.0702;
    // IL (Illinois) 600-629
    if (prefix >= 600 && prefix <= 629) return 0.0881;
    // PA (Pennsylvania) 150-196
    if (prefix >= 150 && prefix <= 196) return 0.0634;
    // OH (Ohio) 430-458
    if (prefix >= 430 && prefix <= 458) return 0.0722;
    // GA (Georgia) 300-319
    if (prefix >= 300 && prefix <= 319) return 0.0735;
    // MI (Michigan) 480-499
    if (prefix >= 480 && prefix <= 499) return 0.0600;
    // NJ (New Jersey) 070-089
    if (prefix >= 70 && prefix <= 89) return 0.0660;
    // WA (Washington) 980-994
    if (prefix >= 980 && prefix <= 994) return 0.0929;
    // AZ (Arizona) 850-865
    if (prefix >= 850 && prefix <= 865) return 0.0837;
    // MA (Massachusetts) 010-027
    if (prefix >= 10 && prefix <= 27) return 0.0625;
    // TN (Tennessee) 370-385
    if (prefix >= 370 && prefix <= 385) return 0.0955;
    // NV (Nevada) 889-898
    if (prefix >= 889 && prefix <= 898) return 0.0823;
    // CO (Colorado) 800-816
    if (prefix >= 800 && prefix <= 816) return 0.0772;

    return 0.065; // National Average fallback
};

// --- Image Compression Helper ---
export const compressImage = (file, maxWidth, quality) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event) => {
            const img = new Image();
            img.src = event.target.result;
            img.onload = () => {
                const canvas = document.createElement('canvas');
                let width = img.width;
                let height = img.height;

                if (width > maxWidth) {
                    height = Math.round((height * maxWidth) / width);
                    width = maxWidth;
                }

                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);

                canvas.toBlob((blob) => {
                    resolve(new File([blob], file.name, { type: 'image/jpeg' }));
                }, 'image/jpeg', quality);
            };
            img.onerror = (err) => reject(err);
        };
        reader.onerror = (err) => reject(err);
    });
};

// --- Email & SMS Sending Helper ---
export const sendEmail = async (to, subject, html, phone = null, smsText = null) => {
    try {
        const sendBrevoNotification = httpsCallable(functions, 'sendBrevoNotification');
        const textContent = html.replace(/<[^>]*>?/gm, '\n').replace(/\n+/g, '\n').trim();
        
        let cleanPhone = null;
        if (phone) {
            cleanPhone = phone.replace(/[^\d+]/g, '');
            if (!cleanPhone.startsWith('+')) {
                cleanPhone = '+1' + cleanPhone;
            }
        }
        
        await sendBrevoNotification({ 
            toEmail: to || null, 
            toPhone: cleanPhone,
            subject: subject, 
            textContent: textContent,
            htmlContent: html,
            smsContent: smsText // Safely pass the shortened text dedicated for SMS
        });
        console.log(`Notification sent successfully to ${to || cleanPhone}`);
    } catch (error) {
        console.error("Failed to send notification via Brevo:", error);
        throw error;
    }
};

// --- AI Parser Helper ---
export const parsePartyDescriptionForFilters = async (description) => {
    const stopWords = new Set(['a', 'an', 'and', 'the', 'in', 'on', 'for', 'with', 'i\'m', 'i', 'is', 'are', 'of', 'to', 'it', 'some', 'my', 'hosting', 'about', 'at']);

    const cleanedDescription = description.toLowerCase().replace(/[^\w\s]/g, '');
    const words = cleanedDescription.split(/\s+/);
    const initialKeywords = words.filter(word => word.length > 2 && !stopWords.has(word));

    const promptCategories = ['venues', 'tableware', 'food', 'djs', 'mobile-bars', 'tents', 'tables', 'decorations', 'entertainment', 'audio-visual', 'photography', 'videography', 'content-creation', 'packages', 'florals', 'staffing', 'transportation', 'bakery', 'planners', 'mobile-restrooms'];
    
    const categoryMap = {
        'tents': 'tents-canopies', 'tent': 'tents-canopies', 'canopies': 'tents-canopies', 'canopy': 'tents-canopies',
        'tables': 'tables-chairs', 'table': 'tables-chairs', 'chairs': 'tables-chairs', 'chair': 'tables-chairs',
        'balloons': 'decorations', 'balloon': 'decorations', 'decor': 'decorations',
        'flowers': 'florals', 'florist': 'florals', 'floral': 'florals',
        'staff': 'staffing', 'bartender': 'staffing', 'server': 'staffing', 'waiter': 'staffing', 'security': 'staffing',
        'limo': 'transportation', 'bus': 'transportation', 'shuttle': 'transportation', 'car': 'transportation',
        'cake': 'bakery', 'dessert': 'bakery', 'cupcakes': 'bakery', 'bakery': 'bakery',
        'video': 'videography', 'videographer': 'videography', 'videography': 'videography',
        'content': 'content-creation', 'content creator': 'content-creation', 'tiktok': 'content-creation', 'social media': 'content-creation', 'reels': 'content-creation',
        'restrooms': 'mobile-restrooms', 'restroom': 'mobile-restrooms', 'bathroom': 'mobile-restrooms', 'bathrooms': 'mobile-restrooms', 'toilets': 'mobile-restrooms', 'porta-potty': 'mobile-restrooms'
    };
    
    let foundCategory = 'all';
    for (const keyword of initialKeywords) {
        if (promptCategories.includes(keyword)) {
            foundCategory = keyword;
            break; 
        }
        if (categoryMap[keyword]) {
            foundCategory = categoryMap[keyword];
            break;
        }
    }
    if (categoryMap[foundCategory]) foundCategory = categoryMap[foundCategory];

    try {
        const prompt = `Analyze the party description for a rental platform.
1. Extract the location (city and state if possible).
2. Determine the single best category from this list: ${promptCategories.join(', ')}. IMPORTANT: If the description mentions items from multiple different categories (e.g. "tents and tables", or "full party package"), you MUST return "all".
3. Generate a list of 10-15 diverse search keywords. Include specific items mentioned, but ALSO include synonyms, related terms, and broader concepts to ensure similar listings are found.
4. Generate a "searchSummary": A short, conversational, and natural sentence confirming what we are looking for based on the user's description. Avoid robotic or repetitive phrasing like "Searching for [keywords]". Instead, vary the phrasing (e.g., "Let's find some great...", "Here are options for...", "Looking for...", "Checking for..."). Mention the specific event type or key items naturally.

Description: "${description}"`;
        
        const payload = {
            contents: [{ role: "user", parts: [{ text: prompt }] }],
            generationConfig: {
                responseMimeType: "application/json",
                responseSchema: {
                    type: "OBJECT",
                    properties: {
                        keywords: { type: "ARRAY", items: { "type": "STRING" } },
                        location: { "type": "STRING" },
                        category: { "type": "STRING" },
                        searchSummary: { "type": "STRING" }
                    },
                    required: ["keywords", "location", "category", "searchSummary"]
                }
            }
        };
        const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-preview-09-2025:generateContent?key=${GEMINI_API_KEY}`;
        
        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });
        
        const result = await response.json();
        
        if (result.candidates && result.candidates.length > 0) {
            const aiResponseText = result.candidates[0].content.parts[0].text;
            const aiData = JSON.parse(aiResponseText);
            
            let finalCategory = aiData.category;
            if (categoryMap[finalCategory]) finalCategory = categoryMap[finalCategory];

            const validSlugs = ['venues', 'tableware', 'food', 'djs', 'mobile-bars', 'tents-canopies', 'tables-chairs', 'decorations', 'entertainment', 'audio-visual', 'photography', 'videography', 'content-creation', 'packages', 'florals', 'staffing', 'transportation', 'bakery', 'planners', 'mobile-restrooms', 'all'];
            if (!validSlugs.includes(finalCategory)) finalCategory = 'all';
            
            return {
                category: finalCategory,
                keywords: aiData.keywords.join(' '),
                location: aiData.location || '',
                searchSummary: aiData.searchSummary
            };
        }
    } catch (error) {
        console.error("AI keyword extraction failed:", error);
    }

    return {
        category: foundCategory,
        keywords: initialKeywords.join(' '),
        location: '', 
        searchSummary: `Searching for "${initialKeywords.join(', ')}"`
    };
};